import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { AppStateContext } from '../../../providers/AppStateContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { TrailSystem } from '../../../models/TrailSystem.model'
import { TrailSystemRelations } from '../../../models/TrailSystemRelations.model'

import UserService from '../../../services/UserService/UserService'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'
import { TrailService } from '../../../services/TrailService/TrailService'
import { TrailRelations } from '../../../models/TrailRelations.model'

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import TrailSearch from '../../Trails/TrailSearch/TrailSearch.route'
import Tabs from '../../../components/Tabs/Tabs.component'
import ManualTrailStatusSearch from '../../ManualTrailStatuses/ManualTrailStatusSearch/ManualTrailStatusSearch.route'
import SponsorSearch from '../../Sponsors/SponsorSearch/SponsorSearch.route'
import TrailSystemForm from '../shared/TrailSystemForm.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import { TrailStewardService } from '../../../services/TrailStewardService/TrailStewardService'
import ObstructionReportsSearch from '../../ObstructionReports/ObstructionReportsSearch/ObstructionReportsSearch.route'
import { ConfigService } from '../../../services/ConfigService/ConfigService'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { TrailSystemSectionIds, trailSystemSections } from '../shared/TrailSystemSections'
import { TuningProfileService } from '../../../services/TuningProfileService/TuningProfileService'
import { TuningProfile } from '../../../models/TuningProfile.model'
import TuningProfilesSection from '../shared/TuningProfiles/TuningProfilesSection.component'

const TrailSystemDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const formattedUser = useMemo(() => (user ? UserService.formatUser(user) : undefined), [user])
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editTrailSystem])
  const hasWeatherGroupPermissions = UserService.hasPermissions(user, [AppPermissions.viewWeatherGroup])
  const hasTrailPermissions = UserService.hasPermissions(user, [AppPermissions.viewTrail])
  const hasSponsorPermissions = UserService.hasPermissions(user, [AppPermissions.viewSponsor])
  const hasManualTrailStatusPermissions = UserService.hasPermissions(user, [AppPermissions.viewManualTrailStatus])
  const hasObstructionReportPermissions = UserService.hasPermissions(user, [AppPermissions.viewObstructionReport])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const trailSystemService = useMemo(() => new TrailSystemService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Trail System Detail`)
  }, [id])

  const queryClient = useQueryClient()
  // hook to get the precipitation lookback days for the region if not given for the trail system

  const { data: regionConfig } = useQuery(['ConfigService.get'], () => new ConfigService().get())
  const { isLoading, data: trailSystem } = useQuery(
    [
      'TrailSystemService.get',
      {
        id: Number(id),
        relations: hasWeatherGroupPermissions
          ? [TrailSystemRelations.weatherGroups, TrailSystemRelations.trailStewards, TrailSystemRelations.parkingLots]
          : [TrailSystemRelations.parkingLots],
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailSystemService.get({
        id: Number(id),
        relations: hasWeatherGroupPermissions
          ? [TrailSystemRelations.weatherGroups, TrailSystemRelations.trailStewards, TrailSystemRelations.parkingLots]
          : [TrailSystemRelations.parkingLots],
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load trail system.')
        console.error(err)
      },
    },
  )

  // keyed on the id in the URL rather than the loaded trail system, so the list loads alongside it
  // instead of waiting for it
  const tuningProfilesQueryKey = [
    'TuningProfileService.list',
    { trailSystemId: Number(id) },
    AppStateContext.getRegion(),
  ]
  const { data: tuningProfiles, isLoading: isLoadingTuningProfiles } = useQuery(
    tuningProfilesQueryKey,
    () => new TuningProfileService().list(Number(id)),
    {
      enabled: !!Number(id),
      onError: (err) => {
        SnackbarContext.show(typeof err === 'string' ? err : 'Failed to load tuning profiles.', 'error')
        console.error(err)
      },
    },
  )
  const activeTuningProfile = tuningProfiles?.find((profile) => profile.active)

  // Trailforks mapping coverage: only fetched when this system has Trailforks sync enabled.
  // Shows admins how many trails in the system have at least one Trailforks Trail ID mapped.
  const trailService = useMemo(() => new TrailService(), [])
  const { data: trailforksMappingResponse } = useQuery(
    ['TrailService.search.trailforksMapping', { systemId: trailSystem?.id }, AppStateContext.getRegion()],
    () =>
      trailService.search({
        systemIds: trailSystem?.id ? [trailSystem.id] : undefined,
        page: 1,
        pageSize: 1000,
        relations: [TrailRelations.trailforksIds],
      }),
    {
      enabled: !!trailSystem?.id && !!trailSystem?.trailforksSyncEnabled,
    },
  )

  const trailforksMappingCoverage = useMemo(() => {
    const rows = trailforksMappingResponse?.data
    if (!rows) return undefined
    const total = rows.length
    const mapped = rows.filter((t) => (t.trailforksIds?.length ?? 0) > 0).length
    return { total, mapped }
  }, [trailforksMappingResponse])

  const trailStewardService = useMemo(() => new TrailStewardService(), [])
  const { data: trailStewardResponse } = useQuery(
    [
      'TrailStewardService.search',
      {
        systemId: trailSystem?.id,
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailStewardService.search({
        page: 1,
        pageSize: 200,
        orderBy: 'createdAt',
        orderDirection: 'DESC',
      }),
    {
      onError: (err) => {
        SnackbarContext.show(typeof err === 'string' ? err : 'An error occurred while searching for sponsors.', 'error')
        console.error(err)
      },
    },
  )

  const trailStewardNames = useMemo(() => {
    const tempNames = trailStewardResponse?.data
      .map((eachTrailSteward) => trailSystem?.trailStewards?.includes(eachTrailSteward.id) && eachTrailSteward.name)
      .filter((name) => name !== false)
    return tempNames === undefined ? [] : tempNames
  }, [trailStewardResponse, trailSystem])

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (trailSystem?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      trailSystemService
        .delete(trailSystem.id)
        .then(() => {
          SnackbarContext.show(
            `Trail System deleted successfully. You will now be redirected.`,
            'success',
            APP_UPDATE_NOTICE,
          )

          // after a delay of 1 second, invalidate cache and redirect to the trailSystem list page
          setTimeout(() => {
            navigate('/trail-systems')
            queryClient.removeQueries([
              'TrailSystemService.get',
              {
                id: Number(id),
                relations: hasWeatherGroupPermissions ? [TrailSystemRelations.weatherGroups] : undefined,
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Trail System failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const tabs = useMemo(() => {
    const tabs = []
    if (trailSystem) {
      if (hasTrailPermissions) {
        tabs.push({
          label: 'Trails',
          content: (
            <TrailSearch nested systemId={trailSystem.id} hideBreadcrumbs hideHeader excludeColumns={['system']} />
          ),
        })
      }
      if (hasManualTrailStatusPermissions) {
        tabs.push({
          label: 'Manual Statuses',
          content: <ManualTrailStatusSearch nested systemId={trailSystem.id} hideHeader excludeColumns={['system']} />,
        })
      }
      if (hasSponsorPermissions) {
        tabs.push({
          label: 'Sponsors',
          content: <SponsorSearch nested systemId={trailSystem.id} hideHeader excludeColumns={['system']} />,
        })
      }
      if (hasObstructionReportPermissions) {
        tabs.push({
          label: 'Obstruction Reports',
          content: <ObstructionReportsSearch nested systemId={trailSystem.id} hideHeader excludeColumns={['system']} />,
        })
      }
    }
    return tabs
  }, [
    trailSystem,
    hasTrailPermissions,
    hasManualTrailStatusPermissions,
    hasSponsorPermissions,
    hasObstructionReportPermissions,
  ])

  const inAppUrl = trailSystem
    ? `https://${AppStateContext.getRegion().toLowerCase()}.flowfeed.app/trail-system/${trailSystem.permalink}`
    : ''
  const sortedWeatherGroups = [...(trailSystem?.weatherGroups ?? [])].sort((a, b) => a.priority - b.priority)
  const sectionById = (sectionId: TrailSystemSectionIds) =>
    trailSystemSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: TrailSystemSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {trailSystem && (
        <FormDialog<TrailSystem> open={isEditing} entity={trailSystem} entityLabel="Trail System" onClose={closeEditor}>
          <TrailSystemForm
            trailSystem={trailSystem}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this trail system?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && trailSystem && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={[{ label: 'Trail Systems', href: '/trail-systems' }, { label: trailSystem.name }]}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      {formattedUser && !formattedUser.trailSystems.length && (
                        <Button variant="text" onClick={handleDeleteClick}>
                          Delete
                        </Button>
                      )}
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <DetailHeader
                  title={trailSystem.name}
                  active={trailSystem.active}
                  subtitle={`${trailSystem.region} · ID ${trailSystem.id}${
                    trailSystem.city ? ` · ${trailSystem.city}, ${trailSystem.state}` : ''
                  }`}
                  link={{ href: inAppUrl }}
                  meta={[
                    `Sort Weight: ${trailSystem.sortWeight}`,
                    `Updated ${formatDate(trailSystem.updatedAt)}`,
                    `Created ${formatDate(trailSystem.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- Location ------------------------------------------------ */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailSystemSectionIds.location)}
                      onEdit={editSectionHandler(TrailSystemSectionIds.location)}
                    >
                      <DetailField label="Address">
                        <Box>
                          {trailSystem.street}
                          <br />
                          {trailSystem.city}, {trailSystem.state}
                        </Box>
                      </DetailField>
                      <DetailField label="Parking Lots">
                        {trailSystem.parkingLots && trailSystem.parkingLots.length > 0 ? (
                          <Stack spacing={1} sx={{ width: '100%' }}>
                            {trailSystem.parkingLots.map((parkingLot, index) => (
                              <Stack
                                key={parkingLot.id || index}
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                                sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                              >
                                <LocalParkingIcon fontSize="small" sx={{ color: 'grey.500', mt: 0.25 }} />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body1">
                                    {parkingLot.name || `Parking Lot ${index + 1}`}
                                  </Typography>
                                  <Link
                                    variant="body2"
                                    component="a"
                                    href={`https://www.google.com/maps/search/?api=1&query=${parkingLot.gpsLat},${parkingLot.gpsLong}`}
                                    target="_blank"
                                  >
                                    {parkingLot.gpsLat}, {parkingLot.gpsLong}
                                  </Link>
                                </Box>
                              </Stack>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body1" color="grey.500">
                            No parking lots
                          </Typography>
                        )}
                      </DetailField>
                    </DetailSection>
                  </Grid>

                  {/* --- Weather & Algorithm ------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailSystemSectionIds.weather)}
                      onEdit={editSectionHandler(TrailSystemSectionIds.weather)}
                    >
                      {hasWeatherGroupPermissions && (
                        <>
                          <DetailField label="Algorithm Weather Groups (by priority)">
                            {sortedWeatherGroups.length > 0 ? (
                              <Box component="ol" sx={{ m: 0, pl: 3 }}>
                                {sortedWeatherGroups.map((wg) => (
                                  <li key={wg.id}>
                                    <Link href={`/weather-groups/${wg.id}`}>{wg.name}</Link>
                                  </li>
                                ))}
                              </Box>
                            ) : (
                              'None'
                            )}
                          </DetailField>
                          <DetailField label="Display Weather Group">
                            {trailSystem.displayWeatherGroup ? (
                              <Link href={`/weather-groups/${trailSystem.displayWeatherGroup.id}`}>
                                {trailSystem.displayWeatherGroup.name}
                              </Link>
                            ) : (
                              'None'
                            )}
                          </DetailField>
                          <Divider />
                        </>
                      )}
                      {activeTuningProfile && (
                        <DetailField
                          label="Tuning Profile"
                          note={`Edits to these values save into ${activeTuningProfile.name}`}
                        >
                          {/* a chip fills its line where text leaves room under the letters, so add that room back
                          to keep the note below as far away as on the other fields */}
                          <Chip size="small" color="primary" label={activeTuningProfile.name} sx={{ mb: 0.5 }} />
                        </DetailField>
                      )}
                      <DetailFieldGrid>
                        <DetailField
                          label="Precipitation Lookback"
                          inheritedFrom={
                            typeof trailSystem.precipitationLookbackDays === 'number'
                              ? undefined
                              : regionConfig?.precipitationLookbackDays
                              ? 'Region'
                              : 'Default'
                          }
                        >
                          {typeof trailSystem.precipitationLookbackDays === 'number'
                            ? trailSystem.precipitationLookbackDays
                            : regionConfig?.precipitationLookbackDays || 7}{' '}
                          days
                        </DetailField>
                        <DetailField
                          label="Dry-Time Modifier"
                          inheritedFrom={
                            trailSystem.dryTimeModifier === null || trailSystem.dryTimeModifier === undefined
                              ? 'Default'
                              : undefined
                          }
                        >
                          {trailSystem.dryTimeModifier ?? 1}
                        </DetailField>
                        <DetailField
                          label="Saturation Modifier"
                          inheritedFrom={
                            trailSystem.saturationModifier === null || trailSystem.saturationModifier === undefined
                              ? 'Default'
                              : undefined
                          }
                        >
                          {trailSystem.saturationModifier ?? 1}
                        </DetailField>
                        <DetailField
                          label="Freeze/Thaw"
                          inheritedFrom={
                            trailSystem.freezeThawEnabled === null || trailSystem.freezeThawEnabled === undefined
                              ? 'Region'
                              : undefined
                          }
                        >
                          {(
                            trailSystem.freezeThawEnabled === null || trailSystem.freezeThawEnabled === undefined
                              ? regionConfig?.ftEnabled === 'true'
                              : trailSystem.freezeThawEnabled
                          )
                            ? 'Enabled'
                            : 'Disabled'}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Tuning Profiles ----------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <TuningProfilesSection
                      section={sectionById(TrailSystemSectionIds.tuningProfiles)}
                      trailSystem={trailSystem}
                      profiles={tuningProfiles ?? []}
                      isLoading={isLoadingTuningProfiles}
                      canEdit={hasWritePermissions}
                      onChanged={(updateProfiles) => {
                        // the save's response already holds the changed profile, so update the list in place and
                        // show it straight away instead of refetching it
                        queryClient.setQueryData<TuningProfile[]>(tuningProfilesQueryKey, (current) =>
                          updateProfiles(current ?? []),
                        )
                        // applying or saving a profile changes the system's and its trails' values, so refresh the rest
                        queryClient.invalidateQueries({
                          predicate: (query) => query.queryKey[0] !== 'TuningProfileService.list',
                        })
                      }}
                    />
                  </Grid>

                  {/* --- Trail Stewards ------------------------------------------ */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailSystemSectionIds.stewards)}
                      onEdit={editSectionHandler(TrailSystemSectionIds.stewards)}
                    >
                      {trailStewardNames.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                          {trailStewardNames.map((name, index) => (
                            <Chip key={index} label={name} variant="outlined" />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color={(theme) => theme.palette.error.main}>
                          No steward is attached to this trail system. Add one from the Edit form.
                        </Typography>
                      )}
                    </DetailSection>
                  </Grid>

                  {/* --- Integrations -------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(TrailSystemSectionIds.integrations)}
                      onEdit={editSectionHandler(TrailSystemSectionIds.integrations)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Trailforks Status Sync">
                          <Chip
                            size="small"
                            label={trailSystem.trailforksSyncEnabled ? 'Enabled' : 'Disabled'}
                            color={trailSystem.trailforksSyncEnabled ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </DetailField>
                        <DetailField label="Trailforks Region ID(s)">
                          {trailSystem.trailforksRegionId || '-'}
                        </DetailField>
                      </DetailFieldGrid>
                      {trailSystem.trailforksSyncEnabled && trailforksMappingCoverage && (
                        <DetailField label="Trail Mapping Coverage">
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body1">
                              {trailforksMappingCoverage.mapped} of {trailforksMappingCoverage.total} trails mapped to
                              Trailforks
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={
                                trailforksMappingCoverage.total
                                  ? (trailforksMappingCoverage.mapped / trailforksMappingCoverage.total) * 100
                                  : 0
                              }
                              sx={{ mt: 1, height: 6, borderRadius: 1 }}
                            />
                          </Box>
                        </DetailField>
                      )}
                    </DetailSection>
                  </Grid>
                </Grid>

                {tabs.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Tabs tabs={tabs} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default TrailSystemDetail
